import { windowWidth } from "@styles/global";
import Login from "@assets/Backgrounds/Login.svg";
import LoginMobile from "@assets/Backgrounds/LoginMobile.svg";
import LoginAnimation from "@assets/Backgrounds/LoginAnimation.svg";
import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideInDiag = keyframes`
  from { opacity: 0; transform: translateX(40%); }
  to   { opacity: 1; transform: translateX(0); }
`;

const Container = styled.div`
  background-image: url(${Login});
  background-size: cover;
  background-position: center;
  height: 100%;
  display: flex;

  @media (max-width: ${windowWidth.tablet}) {
    background-image: url(${LoginMobile});
  }
`;

const Panel = styled.div`
  display: flex;
  margin: auto;
  min-height: 400px;
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  h1 {
    margin: 10px 0px;
  }
`;

const Button = styled.button`
  background-color: rgb(0, 41, 81);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  display: inline-block;
  font-size: 1.5rem;
  font-weight: 500;
  min-height: 50px;
  line-height: 1.3333333;
  padding: 10px 24px;
  text-align: center;
  color: #fff;
  box-shadow: 0 0 30px 0 #dae1f7;
  text-transform: uppercase;
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 30px 0px;
`;

const ProductLine = styled.div`
  width: 100%;
  padding: 10px 0px;

  img {
    height: 50px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin: auto;
  width: 470px;
  max-width: 94vw;
  height: 500px;
  max-height: 94vh;
  background-color: white;
  border-radius: 8px;
  padding: 30px 40px;

  & > .container {
    height: auto;
  }

  & > form {
    width: 100%;
    height: 50%;
    justify-content: space-between;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const LoginWrap = styled.div`
  position: relative;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  background: #fff;

  /* Below tablet the diagonal slices are hidden — give the bare form a
     brand-tinted backdrop instead of a flat white screen. Kept light
     enough that the card-less form stays legible. */
  @media (max-width: ${windowWidth.tablet}) {
    background:
      radial-gradient(ellipse 85% 50% at 100% 100%, rgba(255, 183, 51, 0.38), transparent 66%),
      radial-gradient(ellipse 90% 55% at 0% 0%, rgba(41, 104, 168, 0.32), transparent 64%),
      linear-gradient(160deg, #eef3f8 0%, #d9e3ef 55%, #cdd9e8 100%);
  }
`;

const LoginFormArea = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 48px 8%;

  @media (min-width: ${windowWidth.tablet}) {
    padding: 32px 54% 32px 8%;
    align-items: flex-start;
  }
`;

const LoginFormInner = styled.div`
  width: 100%;
  max-width: 460px;
  animation: ${fadeInUp} 0.7s ease-out both;

  form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 32px;
  }
`;

const Diag = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${windowWidth.tablet}) {
    display: block;
  }
`;

const DiagGold = styled(Diag)`
  z-index: 1;
  clip-path: polygon(100% 45%, 100% 100%, 82% 100%);
  background:
    radial-gradient(ellipse 60% 80% at 85% 55%, rgba(255, 245, 170, 0.55), transparent 60%),
    radial-gradient(ellipse at 75% 75%, #FFD633 0%, #F5B81E 35%, #E8A917 65%, #C27C00 100%);
  animation: ${slideInDiag} 0.8s 0.15s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const DiagConcrete = styled(Diag)`
  z-index: 2;
  clip-path: polygon(85% 0, 100% 0, 100% 45%, 82% 100%, 52% 100%);
  background-image: url(${LoginAnimation});
  background-size: 150% 150%;
  background-position: left center;
  background-repeat: no-repeat;
  animation: ${slideInDiag} 0.8s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const DiagBlue = styled(Diag)`
  z-index: 3;
  clip-path: polygon(82% 0, 85% 0, 52% 100%, 49% 100%);
  background:
    linear-gradient(to right, rgba(255, 255, 255, 0.18), transparent 60%),
    linear-gradient(180deg, #0A1F3C 0%, #123A6B 35%, #1A4A82 55%, #0F3060 80%, #081830 100%);
  animation: ${slideInDiag} 0.8s 0.1s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const BtnRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;

  .MuiButton-root {
    background-color: ${(props) => props.theme.brand.navy};

    &:hover {
      background-color: ${(props) => props.theme.brand.navyHover};
    }
  }
`;

export {
  Container,
  Panel,
  Button,
  ProductContainer,
  ProductLine,
  Content,
  LoginWrap,
  LoginFormArea,
  LoginFormInner,
  DiagGold,
  DiagConcrete,
  DiagBlue,
  BtnRow
};
